import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import posthog from 'posthog-js'
import Button from '../../components/Buttons/BaseButton'
import MessageNotice from '../../components/MessageNotice/MessageNotice'
import {
  SUPPORT_CATEGORY_LABELS,
  SUPPORT_STATUS,
} from '../../config/settings/support'
import {
  onFetchAdminSupportTicketsAsync,
  onUpdateSupportTicketStatusAsync,
  removeAdminTicket,
  setAdminStatusFilter,
} from './Support.slice'
import PagePanel from '../../components/Layouts/PagePanel'
import {
  EmptyState,
  FilterChip,
  FilterRow,
  TicketActions,
  TicketBadge,
  TicketCard,
  TicketList,
  TicketMessage,
  TicketMeta,
} from './Support.styles'

const TICKET_EXIT_MS = 280

const categoryLabel = (category) =>
  SUPPORT_CATEGORY_LABELS[category] || category

const formatDate = (value) => {
  if (!value) return ''
  try {
    return new Intl.DateTimeFormat('es-CL', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(value))
  } catch {
    return value
  }
}

const SupportTicketsContainer = () => {
  const dispatch = useDispatch()
  const {
    adminTickets,
    adminStatusFilter,
    updatingTicketId,
    status,
    error,
  } = useSelector((state) => state.support)
  const [exitingIds, setExitingIds] = useState(() => new Set())

  useEffect(() => {
    dispatch(onFetchAdminSupportTicketsAsync({ status: adminStatusFilter }))
  }, [dispatch, adminStatusFilter])

  const handleFilter = (nextStatus) => {
    dispatch(setAdminStatusFilter(nextStatus))
  }

  const handleClose = (id) => {
    if (exitingIds.has(id) || updatingTicketId === id) return

    const leavesOpenList = adminStatusFilter === SUPPORT_STATUS.OPEN
    const startedAt = Date.now()

    if (leavesOpenList) {
      setExitingIds((prev) => {
        const next = new Set(prev)
        next.add(id)
        return next
      })
    }

    dispatch(
      onUpdateSupportTicketStatusAsync({
        id,
        status: SUPPORT_STATUS.CLOSED,
      })
    )
      .unwrap()
      .then(() => {
        posthog.capture('support_ticket_closed')
        if (!leavesOpenList) return

        const remaining = Math.max(0, TICKET_EXIT_MS - (Date.now() - startedAt))
        window.setTimeout(() => {
          dispatch(removeAdminTicket(id))
          setExitingIds((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        }, remaining)
      })
      .catch(() => {
        setExitingIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      })
  }

  const handleReopen = (id) => {
    if (exitingIds.has(id) || updatingTicketId === id) return

    const leavesClosedList = adminStatusFilter === SUPPORT_STATUS.CLOSED
    const startedAt = Date.now()

    if (leavesClosedList) {
      setExitingIds((prev) => {
        const next = new Set(prev)
        next.add(id)
        return next
      })
    }

    dispatch(
      onUpdateSupportTicketStatusAsync({
        id,
        status: SUPPORT_STATUS.OPEN,
      })
    )
      .unwrap()
      .then(() => {
        if (!leavesClosedList) return

        const remaining = Math.max(0, TICKET_EXIT_MS - (Date.now() - startedAt))
        window.setTimeout(() => {
          dispatch(removeAdminTicket(id))
          setExitingIds((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        }, remaining)
      })
      .catch(() => {
        setExitingIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      })
  }

  return (
    <PagePanel
      title="Tickets de soporte"
      body="Revisa mensajes registrados por los usuarios y cierra el ticket cuando quede resuelto."
    >
      <FilterRow>
        <FilterChip
          type="button"
          $active={adminStatusFilter === SUPPORT_STATUS.OPEN}
          onClick={() => handleFilter(SUPPORT_STATUS.OPEN)}
        >
          Abiertos
        </FilterChip>
        <FilterChip
          type="button"
          $active={adminStatusFilter === SUPPORT_STATUS.CLOSED}
          onClick={() => handleFilter(SUPPORT_STATUS.CLOSED)}
        >
          Cerrados
        </FilterChip>
        <FilterChip
          type="button"
          $active={adminStatusFilter === null}
          onClick={() => handleFilter(null)}
        >
          Todos
        </FilterChip>
      </FilterRow>

      {status.fetchAdmin === 'failed' && error && (
        <MessageNotice type="error">{error}</MessageNotice>
      )}

      {status.fetchAdmin === 'loading' && (
        <EmptyState>Cargando tickets...</EmptyState>
      )}

      {status.fetchAdmin === 'succeeded' && adminTickets.length === 0 && (
        <EmptyState>No hay tickets en este filtro.</EmptyState>
      )}

      <TicketList>
        {adminTickets.map((ticket) => (
          <TicketCard key={ticket.id} $exiting={exitingIds.has(ticket.id)}>
            <TicketMeta>
              {ticket.trackingCode && (
                <TicketBadge>{ticket.trackingCode}</TicketBadge>
              )}
              <TicketBadge>{categoryLabel(ticket.category)}</TicketBadge>
              <TicketBadge>{ticket.status}</TicketBadge>
              <span>{formatDate(ticket.createdAt)}</span>
            </TicketMeta>
            <TicketMeta>
              <span>{ticket.profile?.fullName || 'Usuario'}</span>
              {ticket.profile?.email && <span>{ticket.profile.email}</span>}
            </TicketMeta>
            <TicketMessage>{ticket.message}</TicketMessage>
            <TicketActions>
              {ticket.status === SUPPORT_STATUS.OPEN ? (
                <Button
                  type="button"
                  variant="secondary"
                  width="140px"
                  loading={updatingTicketId === ticket.id}
                  onClick={() => handleClose(ticket.id)}
                >
                  Cerrar
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  width="140px"
                  loading={updatingTicketId === ticket.id}
                  onClick={() => handleReopen(ticket.id)}
                >
                  Reabrir
                </Button>
              )}
            </TicketActions>
          </TicketCard>
        ))}
      </TicketList>
    </PagePanel>
  )
}

export default SupportTicketsContainer
